import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { debounce, Pagination } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { parseAsInteger, useQueryState } from 'nuqs';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetElegibleVotersForElectionFilter } from 'api/hooks/polyas/polyas';
import { downloadVoters } from 'api/hooks/polyas/polyasService';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { translations } from 'locales/i18n';

import { VoterTableSkeleton } from './election-skeletons';
import { VoterTable } from './voter-table';

interface VoterManagementProps {
  electionId: string;
  votedCount: number;
  pendingVotersCount: number;
  totalVotersCount: number;
}

const PAGE_SIZE_OPTIONS = [2, 10, 20, 30, 40];

export function VoterManagement({
  electionId,
  votedCount,
  pendingVotersCount,
  totalVotersCount
}: VoterManagementProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useQueryState('query');
  const [statusFilter, setStatusFilter] = useQueryState('state', { defaultValue: 'all' });
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useState(10);

  function mapHasVoted(status: string) {
    if (status === 'voted') return true;
    else if (status === 'pending') return false;
    else return undefined;
  }

  const {
    data: voters,
    isLoading: isVotersLoading,
    error: votersError,
    refetch
  } = useGetElegibleVotersForElectionFilter(electionId, {
    params: { hasVoted: mapHasVoted(statusFilter), page: page, limit: limit, name: searchQuery }
  });

  const debouncedChangeHandler = useCallback(
    debounce((text: string) => {
      if (text === '') {
        setSearchQuery(null);
      } else {
        setSearchQuery(text);
        setPage(1);
      }
    }, 1000),
    []
  );

  if (!voters || isVotersLoading) {
    return <VoterTableSkeleton />;
  }

  // const votedCount = voters.data.filter(v => v.hasElectionMarkedAsVoted).length;
  // const pendingCount = voters.data.length - votedCount;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${totalVotersCount} ${t(translations.pages.polyas.voter.management.voters())}`}
            variant="outlined"
            size="small"
          />
          <Chip
            label={`${votedCount} ${t(translations.pages.polyas.voter.management.voted())}`}
            color="success"
            size="small"
          />
          <Chip
            label={`${pendingVotersCount} ${t(
              translations.pages.polyas.voter.management.pending()
            )}`}
            size="small"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => {
              handleDownload('voter-export.xlsx', downloadVoters(electionId));
            }}
          >
            {t(translations.pages.polyas.voter.management.polyasExport())}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <TextField
          placeholder={t(translations.pages.polyas.voter.management.searchPlaceholder())}
          onChange={e => debouncedChangeHandler(e.target.value)}
          defaultValue={searchQuery}
          size="small"
          sx={{ flex: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }
          }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t(translations.pages.polyas.voter.management.status())}</InputLabel>
          <Select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            label={t(translations.pages.polyas.voter.management.status())}
          >
            <MenuItem value="all">
              {t(translations.pages.polyas.voter.management.allStatuses())}
            </MenuItem>
            <MenuItem value="voted">
              {t(translations.pages.polyas.voter.management.votedStatus())}
            </MenuItem>
            <MenuItem value="pending">
              {t(translations.pages.polyas.voter.management.pendingStatus())}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {voters.data.length !== voters.meta.total && (
        <Typography variant="body2" color="text.secondary">
          {t(translations.pages.polyas.voter.management.showing())} {voters.data.length}{' '}
          {t(translations.pages.polyas.voter.management.of())} {voters.meta.total}{' '}
          {t(translations.pages.polyas.voter.management.votersCount())}
        </Typography>
      )}

      <VoterTable voters={voters.data} />
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
        <Pagination
          count={voters.meta.totalPages}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
          size="small"
          color="primary"
          showFirstButton
          showLastButton
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>
    </Box>
  );
}
